/**
 * Provenance DAG API — fetches nodes and edges for the project graph.
 */

import { apiClient } from "./client";

export interface DAGNodeMetadata {
  flow360_case_id?: string;
  base_case_id?: string;
  flow360_volume_mesh_id?: string;
  flow360_surface_mesh_id?: string;
  flow360_geometry_id?: string;
  status?: string;
  sweep_id?: string;
  sweep_params?: string[];
  [key: string]: unknown;
}

export interface DAGNode {
  id: string;
  path: string;
  kind: string;
  name: string;
  type: string;
  metadata?: DAGNodeMetadata;
  value_json?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface DAGEdge {
  id: string;
  parent_id: string;
  child_id: string;
  relationship?: string;
  operation?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
  // Mirror DAGNode's open shape so a DAGEdge satisfies the graph adapters'
  // ProvenanceEdgeInput (which carries `[key: string]: unknown`).
  [key: string]: unknown;
}

export interface DAGData {
  nodes: DAGNode[];
  edges: DAGEdge[];
}

export interface FetchLineageOptions {
  scope?: "all" | "runs";
  includePlots?: boolean;
  detail?: "full" | "dag";
  /**
   * FXC-8788: "operations" fetches the real V3 operation graph from
   * `/api/v2/graph/v3-dag` (FXC-8786) — operations as first-class nodes with
   * tool + actor and typed input/output bindings — instead of the flat
   * `/nodes/dag` artifact graph. The endpoint is fallback-safe: it serves flat
   * V1 artifacts until the V3 ingest runs + `THREAD_V3_READS_*` flip on, then
   * auto-upgrades to the operation DAG. The RoCS wedge uses this so the chain
   * reads geometry [input_to] meshing_op [outputs] mesh [input_to] solve. Default "dag".
   */
  mode?: "dag" | "operations";
}

export async function fetchLineage(
  projectId: string,
  branch = "main",
  options: FetchLineageOptions = {},
): Promise<DAGData> {
  // FXC-8788: the RoCS wedge wants the real V3 operation graph, not the flat
  // /nodes/dag artifact graph. Re-point to /api/v2/graph/v3-dag for that surface.
  if (options.mode === "operations") return fetchV3OperationDag(projectId, branch);
  const branchParam = encodeURIComponent(branch);
  const { scope, includePlots = true, detail } = options;
  const scopeParam = scope ? `&scope=${scope}` : "";
  const plotsParam = includePlots ? "&include_plots=true" : "";
  const detailParam = detail ? `&detail=${detail}` : "";
  // Single round-trip. `/nodes/dag` resolves the run subgraph once; the old
  // pair of calls made the recursive CTE run twice per DAG load.
  const combined = await apiClient<{
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
  }>(`/nodes/dag?project_id=${projectId}&branch=${branchParam}${plotsParam}${scopeParam}${detailParam}`);
  const rawNodes = combined.nodes;
  const rawEdges = combined.edges;

  const nodes: DAGNode[] = rawNodes.map((n) => {
    const pathStr = String(n.path ?? "");
    const segments = pathStr.split(".");
    return {
      ...n,
      id: String(n.id),
      path: pathStr,
      kind: String(n.kind ?? "unknown"),
      name: String(n.name ?? "") || segments[segments.length - 1] || pathStr,
      type: String(n.kind ?? "unknown"),
    };
  });

  const edges: DAGEdge[] = rawEdges.map((e, i) => ({
    id: String(e.id ?? `edge-${i}`),
    parent_id: String(e.parent_id),
    child_id: String(e.child_id),
    relationship: e.relationship ? String(e.relationship) : undefined,
    operation: e.operation ? String(e.operation) : undefined,
    metadata: e.metadata && typeof e.metadata === "object" ? (e.metadata as Record<string, unknown>) : undefined,
    created_at: e.created_at ? String(e.created_at) : undefined,
  }));

  return { nodes, edges };
}

export interface FetchNeighbourhoodOptions {
  /** Edge-hops out from the focus (both directions). Default 1. */
  depth?: number;
  /** Topology-only light projection (FXC-8641 shape). Default true. */
  light?: boolean;
}

/**
 * Focus-scoped graph neighbourhood for lazy-load (Shuttle FXC-8688 server +
 * Warp FXC-8676 client). Returns the SAME DAGData shape as fetchLineage, so
 * fromProvenanceGraph adapts it identically — <LineageExplorer onRevealRequest>
 * merges it into the working graph on each expand, so the backing graph (which
 * may be millions of nodes) never loads whole.
 */
export async function fetchNeighbourhood(
  projectId: string,
  branch: string,
  focus: string,
  options: FetchNeighbourhoodOptions = {},
): Promise<DAGData> {
  const { depth = 1, light = true } = options;
  const branchParam = encodeURIComponent(branch);
  const res = await apiClient<{
    focus: string;
    depth: number;
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
  }>(`/v2/graph/neighbourhood?project_id=${projectId}&focus=${encodeURIComponent(focus)}&depth=${depth}&branch=${branchParam}&light=${light}`);

  const nodes: DAGNode[] = res.nodes.map((n) => {
    const pathStr = String(n.path ?? "");
    const segments = pathStr.split(".");
    return {
      ...n,
      id: String(n.id),
      path: pathStr,
      kind: String(n.kind ?? "unknown"),
      name: String(n.name ?? "") || segments[segments.length - 1] || pathStr,
      type: String(n.kind ?? "unknown"),
    };
  });
  const edges: DAGEdge[] = res.edges.map((e, i) => ({
    id: String(e.id ?? `edge-${i}`),
    parent_id: String(e.parent_id),
    child_id: String(e.child_id),
    relationship: e.relationship ? String(e.relationship) : undefined,
    operation: e.operation ? String(e.operation) : undefined,
    metadata: e.metadata && typeof e.metadata === "object" ? (e.metadata as Record<string, unknown>) : undefined,
    created_at: e.created_at ? String(e.created_at) : undefined,
  }));
  return { nodes, edges };
}

export async function deleteNode(nodeId: string): Promise<void> {
  await apiClient(`/nodes/${nodeId}`, { method: "DELETE" });
}

export async function restoreNode(nodeId: string): Promise<void> {
  await apiClient(`/nodes/${nodeId}/restore`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// FXC-8788: V3 operation-graph lineage (the real PROV graph)
//
// `/api/v2/graph/v3-dag` (FXC-8786) returns the thread_core operation graph in
// GraphResponse shape — operations as first-class nodes carrying tool + actor,
// every binding typed input/output — and is fallback-safe (flat V1 artifacts,
// operation_count=0, until the V3 ingest runs + THREAD_V3_READS_* flip). We map
// it into the DAGData shape fromProvenanceGraph consumes, so the RoCS wedge
// renders geometry [input_to] meshing_op [outputs] mesh [input_to] solve with
// the tool + engineer on each operation — instead of the flat /nodes/dag graph.
// ---------------------------------------------------------------------------

const V3_ARTIFACT_RENDER_TYPE: Record<string, string> = {
  flow360_geometry: "geometry",
  geometry: "geometry",
  flow360_surface_mesh: "mesh",
  flow360_volume_mesh: "mesh",
  surface_mesh: "mesh",
  volume_mesh: "mesh",
  mesh: "mesh",
  flow360_case_result: "solver_run",
  solver_run: "solver_run",
  run: "solver_run",
  flow360_result_quantity: "quantity_value",
  quantity_value: "quantity_value",
  reference_quantity: "correction",
};

function v3RenderType(nodeType: string, kind: string): string {
  if (nodeType === "operation") return "operation";
  return V3_ARTIFACT_RENDER_TYPE[kind] ?? kind ?? "artifact";
}

// Human-readable node caption (Neo4j-Bloom style: a meaningful property, not the
// raw id). Quantity values show their quantity key (CD, CFzSkinFriction); ops +
// artifacts show a friendly kind name. The raw id is preserved on metadata.ref.
const V3_FRIENDLY_KIND: Record<string, string> = {
  run_cfd_case: "CFD case",
  mesh_geometry: "Meshing",
  geometry: "Geometry",
  surface_mesh: "Surface mesh",
  volume_mesh: "Volume mesh",
  mesh: "Mesh",
  solver_run: "Solver run",
  correction: "Correction",
  reference_quantity: "Reference",
};

function humanLabelFor(kind: string, meta: Record<string, unknown>, rawLabel: string): string {
  if (kind === "quantity_value" || kind === "flow360_result_quantity") {
    const key = meta.quantity_key ?? meta.name;
    return typeof key === "string" && key ? key : "quantity";
  }
  if (kind === "mesh") {
    const sub = meta.mesh_subtype;
    if (sub === "surface_mesh") return "Surface mesh";
    if (sub === "volume_mesh") return "Volume mesh";
    return "Mesh";
  }
  // Generic "artifact" nodes carry their real role in record_subtype
  // (e.g. solver_config → "Solver config") — far more useful than "artifact/<hash>".
  if (kind === "artifact") {
    const sub = meta.record_subtype;
    if (sub === "solver_config") return "Solver config";
    if (typeof sub === "string" && sub) {
      const s = sub.replace(/_/g, " ");
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
  }
  return V3_FRIENDLY_KIND[kind] ?? rawLabel ?? kind;
}

/**
 * Map a V3 GraphResponse (from /v3-dag or /v3-neighbourhood — identical shape)
 * into the DAGData shape fromProvenanceGraph consumes. Operations become
 * first-class nodes carrying tool + actor + engineer (for RocsPedigree); edges
 * are oriented by node-type so artifact --input_to--> op and op --outputs--> artifact.
 */
function mapV3GraphResponse(res: {
  nodes: Record<string, unknown>[];
  edges: Record<string, unknown>[];
}): DAGData {
  // node_type per id, so edges orient correctly regardless of source/target order.
  const nodeTypeById = new Map<string, string>();
  for (const n of res.nodes) nodeTypeById.set(String(n.id), String(n.node_type ?? "artifact"));

  const nodes: DAGNode[] = res.nodes.map((n) => {
    const nodeType = String(n.node_type ?? "artifact");
    const kind = String(n.kind ?? "artifact");
    const meta = (n.metadata_preview && typeof n.metadata_preview === "object")
      ? (n.metadata_preview as Record<string, unknown>)
      : {};
    // Pedigree metadata: surface tool + the real engineer (the operation's actor;
    // also recoverable from operation metadata) for RocsPedigree.
    const metadata: Record<string, unknown> = { ...meta };
    metadata.ref = String(n.label ?? "");
    metadata.content_hash = (n.content_hash as string | null) ?? null;
    if (nodeType === "operation") {
      // tool_name is often null on the V3 op record — fall back to the solver.
      metadata.tool = (n.tool_name as string | null) ?? (meta.solver as string | null) ?? null;
      metadata.tool_version = (n.tool_version as string | null) ?? (meta.solver_version as string | null) ?? null;
      metadata.actor = (n.actor_name as string | null) ?? null;
      metadata.engineer_id = meta.flow360_credential_owner_id ?? meta.flow360_imported_by ?? meta.run_by ?? null;
      metadata.started_at = (n.started_at as string | null) ?? null;
      metadata.completed_at = (n.completed_at as string | null) ?? null;
      metadata.status = (n.status as string | null) ?? null;
      metadata.fidelity = (meta.fidelity as string | null) ?? (meta.fidelity_level as string | null) ?? null;
    }
    return {
      id: String(n.id),
      path: "",
      kind,
      name: humanLabelFor(kind, meta, String(n.label ?? kind)),
      type: v3RenderType(nodeType, kind),
      metadata,
    };
  });

  // Orient by node-type so direction is correct regardless of how the endpoint
  // orders source/target: input  => artifact --input_to--> operation;
  //                       output => operation --outputs--> artifact.
  const edges: DAGEdge[] = res.edges.map((e, i) => {
    const s = String(e.source);
    const t = String(e.target);
    const direction = String(e.direction ?? "");
    const sIsOp = nodeTypeById.get(s) === "operation";
    let parent = s;
    let child = t;
    let relationship: string;
    if (direction === "input") {
      parent = sIsOp ? t : s;
      child = sIsOp ? s : t;
      relationship = "input_to";
    } else if (direction === "output") {
      parent = sIsOp ? s : t;
      child = sIsOp ? t : s;
      relationship = "outputs";
    } else {
      relationship = (typeof e.role === "string" && e.role) ? e.role : (direction || "references");
    }
    return { id: String(e.id ?? `edge-${i}`), parent_id: parent, child_id: child, relationship };
  });

  return { nodes, edges };
}

export async function fetchV3OperationDag(projectId: string, branch: string): Promise<DAGData> {
  const branchParam = encodeURIComponent(branch);
  const res = await apiClient<{
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
  }>(`/v2/graph/v3-dag?project_id=${projectId}&branch=${branchParam}`);
  return mapV3GraphResponse(res);
}

// ---------------------------------------------------------------------------
// FXC-8788: focus-scoped V3 operation neighbourhood (the RoCS wedge surface)
//
// At X-57 scale the full /v3-dag saturates: it is artifact-first ordered
// (created_at, id) over 70k+ artifacts, so the node cap fills with leaf
// quantity_values and NEVER reaches the operations (operation_count=0, edges=0).
// Instead the wedge seeds a focus and walks /v3-neighbourhood (Shuttle FXC-8688
// V3 extension) over thread_core.v3_edge_projection — a bounded, fully-connected
// operation chain (run_cfd_case / mesh_geometry as nodes, geometry/mesh inputs,
// quantity outputs, the real engineer) — then expands on demand. The endpoint is
// fallback-safe: V3 off → V1 resolve_branch_* neighbourhood, same shape.
// ---------------------------------------------------------------------------

// Seed-focus priority: a solver_run centres one run's full chain at shallow depth
// (run_cfd_case op + geometry/mesh inputs + quantity outputs). quantity_value is
// the RoCS "tapped value" and is always present; either resolves the same slice.
const V3_SEED_KIND_PRIORITY = ["solver_run", "run", "volume_mesh", "surface_mesh", "mesh", "geometry", "quantity_value"];

/**
 * Discover a seed focus for the V3 neighbourhood. /v3-dag is artifact-first and
 * never returns operations at scale, but it reliably carries solver_runs in the
 * first rows, so a cheap slice yields a well-connected seed. Returns null only
 * when the project has no V3 (or V1-fallback) nodes at all.
 */
export async function discoverV3SeedFocus(projectId: string, branch: string): Promise<string | null> {
  const branchParam = encodeURIComponent(branch);
  const res = await apiClient<{ nodes: Record<string, unknown>[] }>(
    `/v2/graph/v3-dag?project_id=${projectId}&branch=${branchParam}&limit=200`,
  );
  const nodes = res.nodes ?? [];
  if (nodes.length === 0) return null;
  for (const kind of V3_SEED_KIND_PRIORITY) {
    const hit = nodes.find((n) => String(n.kind ?? "") === kind);
    if (hit) return String(hit.id);
  }
  return String(nodes[0]!.id);
}

export interface FetchV3NeighbourhoodOptions {
  /** Edge-hops out from focus over v3_edge_projection (both directions). 1–6; default 1. */
  depth?: number;
  /** Node + edge cap. Default 20000 (server max 50000). */
  limit?: number;
}

/**
 * Focus-scoped V3 operation neighbourhood (Shuttle FXC-8688 V3 extension):
 * GET /v2/graph/v3-neighbourhood. Same GraphResponse→DAGData mapping as
 * fetchV3OperationDag, so the wedge renders the operation chain without ever
 * loading the full graph. Used both for the initial seed view and each
 * onRevealRequest expand (re-fetch around the new focus).
 */
export async function fetchV3Neighbourhood(
  projectId: string,
  branch: string,
  focus: string,
  options: FetchV3NeighbourhoodOptions = {},
): Promise<DAGData> {
  const { depth = 1, limit = 20000 } = options;
  const branchParam = encodeURIComponent(branch);
  const res = await apiClient<{
    focus: string;
    depth: number;
    nodes: Record<string, unknown>[];
    edges: Record<string, unknown>[];
  }>(
    `/v2/graph/v3-neighbourhood?project_id=${projectId}&focus=${encodeURIComponent(focus)}&depth=${depth}&limit=${limit}&branch=${branchParam}`,
  );
  return mapV3GraphResponse(res);
}

/**
 * Prune a fetched neighbourhood to the focus's lineage SPINE: the operation in
 * the focus's chain, its full ancestry (inputs, transitively — geometry →
 * meshing → mesh → run), and its direct (1-hop) outputs. Drops the fan-out of
 * sibling operations that share an upstream artifact (e.g. the ~2800 CFD runs
 * off one shared mesh), so the chain reads geometry → meshing → mesh → CFD →
 * outputs instead of a hairball.
 */
function lineageSpine(dag: DAGData, focusId: string): DAGData {
  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  const push = (m: Map<string, string[]>, k: string, v: string) => {
    const a = m.get(k);
    if (a) a.push(v); else m.set(k, [v]);
  };
  for (const e of dag.edges) {
    push(children, e.parent_id, e.child_id);
    push(parents, e.child_id, e.parent_id);
  }
  const typeById = new Map(dag.nodes.map((n) => [n.id, n.type] as const));
  const isOp = (id: string) => typeById.get(id) === "operation";
  // Anchor on the operation in the focus's chain: its producing op (parent),
  // else an op it feeds, else the focus node itself.
  const anchor =
    (parents.get(focusId) ?? []).find(isOp) ??
    (children.get(focusId) ?? []).find(isOp) ??
    focusId;
  // Ancestry: walk parents transitively (upstream has no sibling fan-out).
  const keep = new Set<string>([anchor, focusId]);
  const stack = [anchor];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const p of parents.get(cur) ?? []) {
      if (!keep.has(p)) { keep.add(p); stack.push(p); }
    }
  }
  // Direct (1-hop) outputs of the anchor operation.
  for (const c of children.get(anchor) ?? []) keep.add(c);
  // The CFD runs that share this geometry/mesh/config collapse to one run node
  // by default; show the count on that operation ("CFD case ×2,837") — NOT on the
  // inputs (there's only one mesh, one geometry). Expanding it into the individual
  // runs + array-valued outputs is the fuller model (a follow-up).
  const runCount = dag.nodes.filter((n) => n.kind === "run_cfd_case").length;
  return {
    nodes: dag.nodes
      .filter((n) => keep.has(n.id))
      .map((n) =>
        n.id === anchor && runCount > 1
          ? { ...n, name: `${n.name} ×${runCount.toLocaleString()}`, metadata: { ...(n.metadata ?? {}), collapsed_runs: runCount } }
          : n,
      ),
    edges: dag.edges.filter((e) => keep.has(e.parent_id) && keep.has(e.child_id)),
  };
}

const _BACKFILL_ROOT_KINDS = new Set(["geometry", "source_dataset", "reference_quantity"]);
function _isBackfillable(n: DAGNode): boolean {
  return n.type === "operation" || ["mesh", "surface_mesh", "volume_mesh"].includes(n.kind);
}

// Recursively backfill the upstream chain. An operation/mesh whose producer or
// inputs sit deeper than the seed fetched lands with no incoming edge; fetch its
// own 1-hop neighbourhood (cheap -- ops + meshes have few neighbours, unlike a
// shared volume mesh with thousands of run consumers, which already has its
// producer and is never re-fetched), keep just the incoming producer/input edges,
// and repeat to the roots (geometry). Assembles the full real chain:
// geometry -> surface mesher -> surface mesh -> volume mesher -> volume mesh.
async function backfillAncestry(
  projectId: string,
  branch: string,
  dag: DAGData,
): Promise<DAGData> {
  const nodes = [...dag.nodes];
  const edges = [...dag.edges];
  const known = new Map(nodes.map((n) => [n.id, n] as const));
  const edgeKey = (e: DAGEdge) => `${e.parent_id} ${e.child_id}`;
  const haveEdge = new Set(edges.map(edgeKey));
  const fetched = new Set<string>();
  for (let iter = 0; iter < 8; iter++) {
    const hasIncoming = new Set(edges.map((e) => e.child_id));
    const frontier = nodes.filter(
      (n) => !hasIncoming.has(n.id) && !fetched.has(n.id) && !_BACKFILL_ROOT_KINDS.has(n.kind) && _isBackfillable(n),
    );
    if (frontier.length === 0) break;
    let added = false;
    for (const node of frontier) {
      fetched.add(node.id);
      let nbhd: DAGData;
      try {
        nbhd = await fetchV3Neighbourhood(projectId, branch, node.id, { depth: 1, limit: 500 });
      } catch {
        continue;
      }
      const nbhdById = new Map(nbhd.nodes.map((m) => [m.id, m] as const));
      for (const e of nbhd.edges) {
        if (e.child_id !== node.id || haveEdge.has(edgeKey(e))) continue;
        const src = nbhdById.get(e.parent_id);
        if (!src) continue;
        if (!known.has(src.id)) { known.set(src.id, src); nodes.push(src); }
        haveEdge.add(edgeKey(e));
        edges.push(e);
        added = true;
      }
    }
    if (!added) break;
  }
  return { nodes, edges };
}

// Drop transitively-redundant edges so the chain reads minimally: a direct
// geometry -> run edge is removed once geometry already reaches the run through
// the mesh chain (geometry -> surface mesher -> ... -> volume mesh -> run).
function transitiveReduction(dag: DAGData): DAGData {
  const children = new Map<string, string[]>();
  for (const e of dag.edges) {
    const a = children.get(e.parent_id);
    if (a) a.push(e.child_id); else children.set(e.parent_id, [e.child_id]);
  }
  const reachableViaLonger = (from: string, to: string): boolean => {
    const stack = (children.get(from) ?? []).filter((c) => c !== to);
    const seen = new Set(stack);
    while (stack.length) {
      const cur = stack.pop()!;
      if (cur === to) return true;
      for (const nx of children.get(cur) ?? []) if (!seen.has(nx)) { seen.add(nx); stack.push(nx); }
    }
    return false;
  };
  return { nodes: dag.nodes, edges: dag.edges.filter((e) => !reachableViaLonger(e.parent_id, e.child_id)) };
}

// Distinguish the two meshing ops by what they produce: the op that outputs the
// surface mesh is "Surface meshing", the one that outputs the volume mesh is
// "Volume meshing" (both are kind mesh_geometry; only the output tells them apart).
function labelMeshingOps(dag: DAGData): DAGData {
  const byId = new Map(dag.nodes.map((n) => [n.id, n] as const));
  const opLabel = new Map<string, string>();
  for (const e of dag.edges) {
    const parent = byId.get(e.parent_id);
    const child = byId.get(e.child_id);
    if (parent?.kind === "mesh_geometry" && child?.kind === "mesh") {
      const sub = (child.metadata as Record<string, unknown> | undefined)?.mesh_subtype;
      if (sub === "surface_mesh") opLabel.set(parent.id, "Surface meshing");
      else if (sub === "volume_mesh") opLabel.set(parent.id, "Volume meshing");
    }
  }
  if (opLabel.size === 0) return dag;
  return {
    ...dag,
    nodes: dag.nodes.map((n) => (opLabel.has(n.id) ? { ...n, name: opLabel.get(n.id)! } : n)),
  };
}

/**
 * Initial V3 operation view for the RoCS wedge: discover a seed focus, fetch its
 * neighbourhood deep enough to include the meshing operation, prune to the focal
 * run's lineage spine — geometry → meshing → mesh → CFD case → outputs — and
 * backfill any operation's deeper input edges. Avoids the full /v3-dag (which
 * saturates to an operation-free blob at scale) and the sibling-run hairball off
 * a shared mesh. Returns empty when no V3 nodes.
 */
export async function fetchV3OperationSeed(
  projectId: string,
  branch: string,
  options: FetchV3NeighbourhoodOptions = {},
): Promise<DAGData> {
  const focus = await discoverV3SeedFocus(projectId, branch);
  if (!focus) return { nodes: [], edges: [] };
  // Depth 3 reaches the meshing op (geometry → mesh_geometry → mesh → run);
  // lineageSpine prunes the shared mesh's sibling-run fan-out; then backfill the
  // meshing op's input edge (its geometry sits one hop deeper than the fetch).
  const full = await fetchV3Neighbourhood(projectId, branch, focus, {
    depth: options.depth ?? 3,
    limit: options.limit,
  });
  return labelMeshingOps(transitiveReduction(await backfillAncestry(projectId, branch, lineageSpine(full, focus))));
}
